import { checkAdminStatus } from './api';

class AdminStatusService {
  constructor() {
    this.isChecking = false;
    this.cachedStatus = null;
    this.cacheExpiry = null;
    this.cacheDuration = 5 * 60 * 1000; // 5 minutes
    this.listeners = new Set();
  }

  // Add listener for admin status changes
  addListener(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  // Notify all listeners of status change
  notifyListeners(isAdmin) {
    this.listeners.forEach(callback => callback(isAdmin));
  }

  // Check if cache is still valid
  isCacheValid() {
    return this.cachedStatus !== null && 
           this.cacheExpiry && 
           Date.now() < this.cacheExpiry;
  }

  // Get admin status with caching and race condition prevention
  async getAdminStatus(userId, forceRefresh = false) {
    // Return cached result if valid and not forcing refresh
    if (!forceRefresh && this.isCacheValid()) {
      return this.cachedStatus;
    }

    // Prevent multiple simultaneous checks
    if (this.isChecking) {
      return new Promise((resolve) => {
        const unsubscribe = this.addListener((isAdmin) => {
          unsubscribe();
          resolve(isAdmin);
        });
      });
    }

    this.isChecking = true;

    try {
      const result = await checkAdminStatus(userId);
      const isAdmin = result?.data?.is_admin === true;
      
      // Cache the result
      this.cachedStatus = isAdmin;
      this.cacheExpiry = Date.now() + this.cacheDuration;
      
      // Notify all listeners
      this.notifyListeners(isAdmin);
  
      return isAdmin;
    } catch (error) {
      console.error('Failed to check admin status:', error);
      
      // If we have a cached result, use it as fallback
      if (this.cachedStatus !== null) {
        console.warn('Using cached admin status due to error:', this.cachedStatus);
        return this.cachedStatus;
      }
      
      // Default to false if no cache and error occurred
      this.cachedStatus = false;
      this.cacheExpiry = Date.now() + (30 * 1000); // Short cache for errors
      this.notifyListeners(false);
      return false;
    } finally {
      this.isChecking = false;
    }
  }

  // Clear cache (useful for logout or when user permissions change)
  clearCache() {
    this.cachedStatus = null;
    this.cacheExpiry = null;
    this.isChecking = false;
  }

  // Get current cached status without making API call
  getCachedStatus() {
    return this.isCacheValid() ? this.cachedStatus : null;
  }
}

// Export singleton instance
export const adminStatusService = new AdminStatusService();
export default adminStatusService;
